import axios from 'axios'
import {getAdminToken} from '../auth/_requests'

const API_URL = process.env.REACT_APP_API_URL

const adminApi = axios.create({withCredentials: true})
adminApi.interceptors.request.use((config) => {
  const token = getAdminToken()
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

const BASE = `${API_URL}/admin/fb-ads`

export interface Overview {
  account_id: string
  page_id: string
  pixel_id: string | null
  capi_enabled: boolean
  account_status: number
  currency: string
  balance: number
  amount_spent: number
  today: {spend: string; impressions: string; clicks: string}
  last_7d: {spend: string; impressions: string; clicks: string}
}

export interface Reel {
  slug: string
  name: string
  concept: string
  secs: number
  mp4: string
  poster: string
  ad: {headline: string; primary: string}
  targeting: {age: string; interests: string[]}
}

export interface Funnel {
  signups: number
  campaign?: number
  post?: number
  connected: number
  paid: number
  revenue: number
  wa_conversations?: number
  form_leads?: number
}

export interface Launch {
  _id: string
  slug: string
  name: string
  headline: string
  link: string
  daily_budget: number
  destination?: 'website' | 'lead_form' | 'whatsapp' | 'other'
  audience?: string
  source?: 'created' | 'imported'
  test?: string
  creative_key?: string
  video_id?: string
  thumb?: string | null
  mp4?: string | null
  budget_level?: 'adset' | 'campaign'
  has_lifetime_budget?: boolean
  status: 'creating' | 'PAUSED' | 'ACTIVE' | 'failed'
  error?: string
  ad_id?: string
  createdAt: string
  meta: null | {
    effective_status?: string
    issues?: string[]
    spend?: number
    impressions?: number
    clicks?: number
    ctr?: number
    cpm?: number
    fb_registrations?: number
  }
  funnel: Funnel
}

export interface AdSignup {
  _id: string
  name: string
  phone: string
  email: string
  created_at: string
  trial_period_end: string
  source: string
  ad_id: string
  ad_name: string
  connected: boolean
  revenue: number
}

export const getOverview = () =>
  adminApi.get<{success: boolean; configured: boolean; overview?: Overview}>(`${BASE}/overview`)
export const getReels = () =>
  adminApi.get<{success: boolean; reels: Reel[]; default_link: string}>(`${BASE}/reels`)
export const getLaunches = () =>
  adminApi.get<{success: boolean; launches: Launch[]}>(`${BASE}/launches`)
export const getSignups = () =>
  adminApi.get<{success: boolean; signups: AdSignup[]; by_source: Record<string, Funnel>}>(
    `${BASE}/signups`
  )
export const launchReel = (body: {
  creative_key: string
  daily_budget: number
  headline: string
  primary: string
  link: string
  destination: 'website' | 'lead_form' | 'whatsapp'
  audience: 'broad' | 'lal_1' | 'lal_3'
}) => adminApi.post<{success: boolean; error?: string}>(`${BASE}/launch`, body)
export const setLaunchStatus = (id: string, status: 'ACTIVE' | 'PAUSED') =>
  adminApi.post<{success: boolean}>(`${BASE}/launches/${id}/status`, {status})
export const setLaunchBudget = (id: string, daily_budget: number) =>
  adminApi.post<{success: boolean}>(`${BASE}/launches/${id}/budget`, {daily_budget})
export const removeLaunch = (id: string) =>
  adminApi.delete<{success: boolean}>(`${BASE}/launches/${id}`)

export interface AudienceInfo {
  id: string
  name?: string
  approximate_count_lower_bound?: number
  approximate_count_upper_bound?: number
  operation_status?: {code: number; description: string}
  error?: string
}
export interface FbLeadRow {
  _id: string
  name: string
  phone: string
  email: string
  ad_id: string
  lead_created_at: string
  status: 'created' | 'existing' | 'failed'
  error?: string
  account?: string
}
export const getAudiences = () =>
  adminApi.get<{
    success: boolean
    audiences: Record<string, AudienceInfo>
    last_synced_at: string | null
    last_result: any
  }>(`${BASE}/audiences`)
export const syncAudiences = () =>
  adminApi.post<{success: boolean; result: any}>(`${BASE}/audiences/sync`)
export const getLeads = () => adminApi.get<{success: boolean; leads: FbLeadRow[]}>(`${BASE}/leads`)
export const pollLeads = () => adminApi.post<{success: boolean; result: any}>(`${BASE}/leads/poll`)

export interface EmailStep {
  enabled: boolean
  after_hours: number
  subject: string
  title: string
  body: string
  cta: string
}
export interface LeadFormTexts {
  name: string
  intro_title: string
  intro_items: string[]
  intro_button: string
  thanks_title: string
  thanks_body: string
  thanks_button: string
  thanks_url: string
}
export interface WhatsappBot {
  enabled: boolean
  greeting: string
  ask_business: string
  ask_cities: string
  campaigns_created: string
  created: string
  logged_in: string
  full_tutorial_video?: string
  full_tutorial_caption?: string
  step_delay_sec: number
  assistant_enabled: boolean
  existing: string
  tutorial_video: string
  tutorial_caption: string
  connect_video: string
  connect_caption: string
  connect_step_enabled?: boolean
  test_numbers: string[]
  reset_command: string
}
export interface Flow {
  trial_days: number
  default_link: string
  lead_form: LeadFormTexts
  whatsapp_bot: WhatsappBot
  emails: {enabled: boolean; steps: EmailStep[]}
}
export interface FlowResponse {
  success: boolean
  flow: Flow
  defaults: Flow
  welcome_template: string
  welcome_default: string
  lead_form: {form_id: string | null; form_ids: string[]; form_created_at: string | null}
  reminders_cron_on: boolean
  leads_poll_on: boolean
}
export const getFlow = () => adminApi.get<FlowResponse>(`${BASE}/flow`)
export const saveFlow = (flow: Flow) =>
  adminApi.put<{success: boolean; flow: Flow}>(`${BASE}/flow`, flow)
export const saveWelcome = (template: string) =>
  adminApi.put<{success: boolean; welcome_template: string}>(`${BASE}/flow/welcome`, {template})
export const testWhatsapp = (phone: string, template: string) =>
  adminApi.post<{success: boolean; to: string}>(`${BASE}/flow/test-whatsapp`, {phone, template})
export const testEmail = (to: string, index: number, step: EmailStep) =>
  adminApi.post<{success: boolean; to: string}>(`${BASE}/flow/test-email`, {to, index, step})
export const newLeadForm = () =>
  adminApi.post<{success: boolean; form_id: string}>(`${BASE}/flow/lead-form`)

export const importExistingAds = () =>
  adminApi.post<{
    success: boolean
    total: number
    skipped_hiring: number
    removed_hiring: number
    added: number
    updated: number
    forms: any
  }>(`${BASE}/import-existing`)

export interface WaContact {
  _id: string
  wa_id: string
  name: string
  first_message_at: string
  last_message_at: string
  last_text: string
  last_direction: 'in' | 'out'
  unread: number
  message_count: number
  referral?: {ad_id?: string; headline?: string; source_url?: string; ctwa_clid?: string}
  account?: string
  bot_state?: string
  ad_name: string | null
  account_name: string | null
  window_open: boolean
}
export interface WaMessage {
  _id: string
  direction: 'in' | 'out'
  text: string
  type: string
  at: string
  status?: string
  error?: string
  referral_ad_id?: string
  message_id?: string
  media_id?: string
  transcript?: string
}
// A customer's voice note / image, as a Blob (the request needs the admin token, so it can't
// be a plain <audio src>).
export const getWaMedia = (messageId: string) =>
  adminApi.get<Blob>(`${BASE}/wa/messages/${encodeURIComponent(messageId)}/media`, {responseType: 'blob'})
export const transcribeWa = (messageId: string) =>
  adminApi.post<{success: boolean; transcript: string}>(
    `${BASE}/wa/messages/${encodeURIComponent(messageId)}/transcribe`
  )
export const getWaContacts = (q: string, fromAds: boolean) =>
  adminApi.get<{success: boolean; contacts: WaContact[]; unread_total: number}>(
    `${BASE}/wa/contacts`,
    {
      params: {q: q || undefined, from_ads: fromAds ? '1' : undefined},
    }
  )
export const getWaMessages = (waId: string) =>
  adminApi.get<{success: boolean; messages: WaMessage[]}>(`${BASE}/wa/contacts/${waId}/messages`)
export const sendWaReply = (waId: string, text: string) =>
  adminApi.post<{success: boolean; message_id: string}>(`${BASE}/wa/contacts/${waId}/reply`, {text})
export const resetTestNumber = (phone: string) =>
  adminApi.post<{success: boolean; accounts_retired: number; messages_deleted: number}>(
    `${BASE}/flow/reset-test`,
    {phone}
  )

export interface Creative {
  key: string
  kind: 'reel' | 'video'
  slug?: string
  video_id?: string
  title: string
  concept?: string
  headline?: string
  text?: string
  length?: number
  thumb: string
  mp4?: string
  stats: {
    ads: number
    spend: number
    impressions: number
    clicks: number
    leads: number
    cpl: number | null
    ctr: number | null
    cpc: number | null
  }
}
export interface AdTest {
  _id: string
  name: string
  creative_keys: string[]
  destinations: string[]
  audience: string
  daily_budget: number
  cells: {creative_key: string; destination: string; launch: string}[]
  status: 'ACTIVE' | 'PAUSED'
  createdAt: string
  mode?: 'cells' | 'one_ad'
}
export const getCreatives = (refresh: boolean) =>
  adminApi.get<{success: boolean; creatives: Creative[]; rebuilding?: boolean}>(
    `${BASE}/creatives`,
    {
      params: {refresh: refresh ? '1' : undefined},
      timeout: 90000,
    }
  )
export const getVideoSource = (videoId: string) =>
  adminApi.get<{success: boolean; source: string | null}>(
    `${BASE}/creatives/video/${videoId}/source`
  )
export const createTest = (body: {
  creative_keys: string[]
  destinations: string[]
  audience: string
  daily_budget: number
  name: string
  mode: 'cells' | 'one_ad'
  start?: boolean
}) => adminApi.post<{success: boolean; test: AdTest}>(`${BASE}/tests`, body)
export const getTests = () => adminApi.get<{success: boolean; tests: AdTest[]}>(`${BASE}/tests`)
export const setTestStatus = (id: string, status: 'ACTIVE' | 'PAUSED') =>
  adminApi.post<{success: boolean; errors: string[]}>(`${BASE}/tests/${id}/status`, {status})

export interface AssetRow {
  video_id: string
  creative_key: string | null
  spend: number
  impressions: number
  clicks: number
  leads: number
  registrations: number
}
export const getTestAssets = (id: string) =>
  adminApi.get<{success: boolean; by_destination: Record<string, AssetRow[]>}>(
    `${BASE}/tests/${id}/assets`
  )

export interface StuckStep {
  reached: number
  stuck: number
  watched: number
  helped: number
  stuck_watched: number
  stuck_helped: number
}
export interface StuckRow {
  _id: string
  name: string
  phone: string
  created_at: string
  source: string
  ad_name: string
  stuck_at: 'campaign' | 'post' | 'facebook'
  paid: boolean
  watched: string[]
  helped: string[]
  last_activity: string | null
  device: string | null
}
export interface StuckResponse {
  success: boolean
  days: number
  summary: {signups: number; steps: Record<'campaign' | 'post' | 'facebook', StuckStep>}
  rows: StuckRow[]
}
export const getStuck = (days: number, source: 'all' | 'ads') =>
  adminApi.get<StuckResponse>(`${BASE}/onboarding-stuck`, {params: {days, source}})

export type WaTemplateRow = {name: string; use: string; status: string; category: string; rejected_reason: string}
export const getWaTemplates = () =>
  adminApi.get<{success: boolean; templates: WaTemplateRow[]}>(`${BASE}/wa/templates`)
export const submitWaTemplates = () =>
  adminApi.post<{success: boolean; results: any[]}>(`${BASE}/wa/templates/submit`)
